import React, { useState } from 'react';
import { FiShield as Shield, FiBook as BookOpen, FiDatabase as Database, FiSettings as Settings, FiX as X, FiCheckSquare as ClipboardCheck } from 'react-icons/fi';

type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * Available scopes for API keys
 * Grouped by resource type for better UX
 *
 * IMPORTANT: Scope format must match backend (read:resource, write:resource)
 * See: lens-genaiops/app/infrastructure/auth.py Scopes class
 */
interface Scope {
  id: string;
  label: string;
  description: string;
  group: string;
  icon: IconComponent;
}

const AVAILABLE_SCOPES: Scope[] = [
  { id: 'read:prompts', label: 'Read Prompts', description: 'View, list, and fetch prompt templates and versions', group: 'Prompts', icon: BookOpen },
  { id: 'write:prompts', label: 'Write Prompts', description: 'Create, update, and manage prompt templates', group: 'Prompts', icon: BookOpen },
  { id: 'read:datasets', label: 'Read Datasets', description: 'View, list, and download test datasets', group: 'Datasets', icon: Database },
  { id: 'write:datasets', label: 'Write Datasets', description: 'Create, update, and upload datasets', group: 'Datasets', icon: Database },
  { id: 'read:configs', label: 'Read Configs', description: 'View system configurations and settings', group: 'Configs', icon: Settings },
  { id: 'write:configs', label: 'Write Configs', description: 'Modify configurations and system settings', group: 'Configs', icon: Settings },
  { id: 'read:evaluations', label: 'Read Evaluations', description: 'View evaluation results and metrics', group: 'Evaluations', icon: ClipboardCheck },
  { id: 'write:evaluations', label: 'Write Evaluations', description: 'Upload evaluation results from SDK', group: 'Evaluations', icon: ClipboardCheck },
  { id: 'admin:*', label: 'Full Access', description: 'All permissions including administrative actions', group: 'Admin', icon: Shield },
];

const SCOPE_GROUPS = ['Prompts', 'Datasets', 'Configs', 'Evaluations', 'Admin'];

const GROUP_COLORS: Record<string, string> = {
  Prompts: 'blue',
  Datasets: 'green',
  Configs: 'purple',
  Evaluations: 'orange',
  Admin: 'red',
};

const GROUP_ICONS: Record<string, IconComponent> = {
  Prompts: BookOpen,
  Datasets: Database,
  Configs: Settings,
  Evaluations: ClipboardCheck,
  Admin: Shield,
};

interface ScopeSelectorProps {
  selectedScopes?: string[] | string;
  onChange: (scopes: string[]) => void;
  error?: string;
}

/**
 * ScopeSelector Component
 *
 * Interactive checkbox-based scope selector with:
 * - Grouped scopes by resource type
 * - Full descriptions for self-guided usage
 * - Quick presets (Read Only, Read/Write, Full Access)
 * - Admin scope auto-selects all
 */
const ScopeSelector = ({ selectedScopes = [], onChange, error }: ScopeSelectorProps) => {
  const [previousScopes, setPreviousScopes] = useState<string[]>([]);

  const isAdminSelected = Array.isArray(selectedScopes)
    ? selectedScopes.includes('admin:*')
    : selectedScopes.split(',').map(s => s.trim()).includes('admin:*');

  // Parse scopes from string if needed
  const scopesArray: string[] = Array.isArray(selectedScopes)
    ? selectedScopes
    : selectedScopes.split(',').filter((s) => s.trim());

  const handleScopeToggle = (scopeId: string) => {
    if (scopeId === 'admin:*') {
      if (isAdminSelected) {
        onChange(previousScopes.length > 0 ? previousScopes : []);
      } else {
        setPreviousScopes(scopesArray.filter((s) => s !== 'admin:*'));
        onChange(AVAILABLE_SCOPES.map((s) => s.id));
      }
    } else {
      if (scopesArray.includes(scopeId)) {
        onChange(scopesArray.filter((s) => s !== scopeId));
      } else {
        onChange([...scopesArray, scopeId]);
      }
    }
  };

  const handlePreset = (preset: string) => {
    switch (preset) {
      case 'readonly':
        onChange(['read:prompts', 'read:datasets', 'read:configs', 'read:evaluations']);
        break;
      case 'readwrite':
        onChange([
          'read:prompts', 'write:prompts',
          'read:datasets', 'write:datasets',
          'read:configs', 'write:configs',
          'read:evaluations', 'write:evaluations'
        ]);
        break;
      case 'fullaccess':
        setPreviousScopes([]);
        onChange(AVAILABLE_SCOPES.map((s) => s.id));
        break;
      case 'clear':
        onChange([]);
        break;
      default:
        break;
    }
  };

  const removeScope = (scopeId: string) => {
    onChange(scopesArray.filter((s) => s !== scopeId));
  };

  const getScopeColor = (scopeId: string): string => {
    const scope = AVAILABLE_SCOPES.find((s) => s.id === scopeId);
    if (!scope) return 'gray';
    return GROUP_COLORS[scope.group] || 'gray';
  };

  const getColorClasses = (color: string): string => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    return colors[color] || colors.gray;
  };

  // Non-admin groups for the grid
  const resourceGroups = SCOPE_GROUPS.filter((g) => g !== 'Admin');

  return (
    <div className="space-y-3">
      {/* Label + Presets in one row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Permissions <span className="text-red-500">*</span>
        </span>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => handlePreset('readonly')}
            className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Read Only
          </button>
          <button
            type="button"
            onClick={() => handlePreset('readwrite')}
            className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Read/Write
          </button>
          <button
            type="button"
            onClick={() => handlePreset('fullaccess')}
            className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300 hover:bg-red-200 transition-colors"
          >
            Full Access
          </button>
        </div>
      </div>

      {/* Scope Grid - 2 columns for resource groups with descriptions */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4">
          {resourceGroups.map((group) => {
            const groupScopes = AVAILABLE_SCOPES.filter((s) => s.group === group);
            const Icon = GROUP_ICONS[group];
            const color = GROUP_COLORS[group];

            return (
              <div key={group} className="space-y-2">
                {/* Group header */}
                <div className={`flex items-center gap-1.5 text-${color}-600 dark:text-${color}-400 pb-1 border-b border-gray-100 dark:border-gray-700`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-semibold">{group}</span>
                </div>
                {/* Scope checkboxes with descriptions */}
                <div className="space-y-2">
                  {groupScopes.map((scope) => {
                    const isChecked = scopesArray.includes(scope.id);
                    const isDisabled = isAdminSelected && scope.id !== 'admin:*';

                    return (
                      <label aria-label={scope.label}
                        key={scope.id}
                        className={`flex items-start gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${isChecked ? 'bg-gray-50 dark:bg-gray-700/30' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => handleScopeToggle(scope.id)}
                          className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200 block">
                            {scope.label.replace(group + ' ', '').replace('Read ', 'Read').replace('Write ', 'Write')}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block leading-tight">
                            {scope.description}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Admin - Full width at bottom */}
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <label
            className={`flex items-start gap-2 cursor-pointer p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ${isAdminSelected ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
          >
            <input
              type="checkbox"
              checked={isAdminSelected}
              onChange={() => handleScopeToggle('admin:*')}
              className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-1"
            />
            <div className="flex items-center gap-2">
              <Shield className={`w-4 h-4 ${isAdminSelected ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`} />
              <div>
                <span className={`text-sm font-medium block ${isAdminSelected ? 'text-red-700 dark:text-red-300' : 'text-gray-800 dark:text-gray-200'}`}>
                  Full Access (admin:*)
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 block">
                  Grants all permissions including administrative actions
                </span>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Selected summary */}
      {scopesArray.length > 0 && !isAdminSelected && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {scopesArray.length} selected:
          </span>
          {scopesArray.slice(0, 6).map((scopeId) => (
            <span
              key={scopeId}
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${getColorClasses(getScopeColor(scopeId))}`}
            >
              {scopeId}
              <button
                type="button"
                onClick={() => removeScope(scopeId)}
                className="ml-1 hover:opacity-70"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {scopesArray.length > 6 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">+{scopesArray.length - 6} more</span>
          )}
        </div>
      )}

      {isAdminSelected && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Full access mode - all permissions are granted
        </p>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Empty hint */}
      {scopesArray.length === 0 && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Select at least one permission for your API key
        </p>
      )}
    </div>
  );
};

export default ScopeSelector;

// Export scope utilities for use in other components
export { AVAILABLE_SCOPES, GROUP_COLORS };
